import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ebdApi } from '../lib/api';
import {
  filterRankingBySalaTipo,
  rankingWorstFirst,
  salaTipo,
} from '../lib/ebdSalaTipo';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function normalizePresencasPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
}

function parseLicaoEstudada(val) {
  if (val === true) return true;
  if (typeof val === 'string') return val.trim() !== '' && val !== 'false' && val !== '0';
  return Boolean(val);
}

function aggregateRows(rows) {
  const presentes = rows.filter((r) => r.presente).length;
  const biblias = rows.filter((r) => r.trouxe_biblia).length;
  const revistas = rows.filter((r) => r.trouxe_revista).length;
  const visitantes = rows.reduce((s, r) => {
    if (r?.visitante_numero != null && !Number.isNaN(Number(r.visitante_numero))) {
      return s + Math.max(0, Math.floor(Number(r.visitante_numero)));
    }
    return s + (r?.trouxe_visitante ? 1 : 0);
  }, 0);
  const licoes = rows.filter((r) => parseLicaoEstudada(r.licao_estudada)).length;
  const datas = new Set(rows.map((r) => r.data_aula).filter(Boolean));
  const somaPontos = rows.reduce((s, r) => s + (Number(r.pontuacao) || 0), 0);
  const registros = rows.length;
  return {
    registros,
    presentes,
    biblias,
    revistas,
    visitantes,
    licoes,
    somaPontos,
    aulasDistintas: datas.size,
  };
}

/** @param {{ presentes: number, aulasDistintas: number }} agg */
function aproveitamentoSala(agg, matriculados) {
  const M = Math.max(0, Number(matriculados) || 0);
  const D = Math.max(0, Number(agg.aulasDistintas) || 0);
  const op = M * D;
  if (op <= 0) return 0;
  return agg.presentes / op;
}

function periodoLabel(periodo, data) {
  const map = { dia: 'Dia', mes: 'Mês', trimestre: 'Trimestre', ano: 'Ano' };
  const base = map[periodo] || periodo;
  if (periodo === 'dia' && data) {
    try {
      return `${base} · ${format(parseISO(data), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}`;
    } catch {
      return `${base} · ${data}`;
    }
  }
  return base;
}

/** @typedef {'intro'|'sala_infantil'|'ranking_intro_infantil'|'ranking_infantil'|'bridge_adulto'|'sala_adulto'|'ranking_intro_adulto'|'ranking_adulto'|'end'} Phase */

const EbdApresentacao = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const periodo = searchParams.get('periodo') || 'mes';
  const dataRef = searchParams.get('data') || format(new Date(), 'yyyy-MM-dd');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salas, setSalas] = useState([]);
  const [ranking, setRanking] = useState([]);
  /** @type {Record<string, any[]>} */
  const [presencasBySala, setPresencasBySala] = useState({});

  /** @type {[Phase, React.Dispatch<React.SetStateAction<Phase>>]} */
  const [phase, setPhase] = useState('intro');
  const [salaIndex, setSalaIndex] = useState(0);
  const [rankingRevealed, setRankingRevealed] = useState(0);

  const pointerStartRef = useRef(null);
  const lastPointerNavRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rankingParams = { igreja_id: user?.igreja_id, periodo };
        if (periodo === 'dia' && dataRef) rankingParams.data = dataRef;

        const [salasRes, rankingRes] = await Promise.all([
          ebdApi.listSalas(user?.igreja_id),
          ebdApi.getRanking(rankingParams),
        ]);
        if (cancelled) return;
        const salasList = salasRes.data || [];
        setSalas(
          salasList.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))
        );
        setRanking(rankingRes.data || []);

        const presParams = { igreja_id: user?.igreja_id };
        if (periodo === 'dia' && dataRef) {
          presParams.data_aula = dataRef;
        } else {
          presParams.periodo = periodo;
          presParams.data = dataRef;
        }

        let allPres = [];
        try {
          const presRes = await ebdApi.listPresencas(presParams);
          allPres = normalizePresencasPayload(presRes.data);
        } catch {
          allPres = [];
        }
        if (cancelled) return;

        const bySala = {};
        for (const p of allPres) {
          const sid = p.sala_id != null ? String(p.sala_id) : null;
          if (!sid) continue;
          if (!bySala[sid]) bySala[sid] = [];
          bySala[sid].push(p);
        }
        setPresencasBySala(bySala);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Não foi possível carregar os dados.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.igreja_id, periodo, dataRef]);

  const salasInfantil = useMemo(
    () => salas.filter((s) => salaTipo(s) === 'infantil'),
    [salas]
  );
  const salasAdulto = useMemo(() => salas.filter((s) => salaTipo(s) === 'adulto'), [salas]);

  const rankInf = useMemo(
    () => filterRankingBySalaTipo(ranking, salas, 'infantil'),
    [ranking, salas]
  );
  const rankAd = useMemo(
    () => filterRankingBySalaTipo(ranking, salas, 'adulto'),
    [ranking, salas]
  );

  const worstInf = useMemo(() => rankingWorstFirst(rankInf), [rankInf]);
  const worstAd = useMemo(() => rankingWorstFirst(rankAd), [rankAd]);

  const advance = useCallback(() => {
    if (loading || error) return;

    switch (phase) {
      case 'intro':
        if (salasInfantil.length > 0) {
          setSalaIndex(0);
          setPhase('sala_infantil');
        } else if (rankInf.length > 0) {
          setRankingRevealed(0);
          setPhase('ranking_intro_infantil');
        } else {
          setPhase('bridge_adulto');
        }
        break;
      case 'sala_infantil':
        if (salaIndex < salasInfantil.length - 1) {
          setSalaIndex((i) => i + 1);
        } else {
          setRankingRevealed(0);
          setPhase('ranking_intro_infantil');
        }
        break;
      case 'ranking_intro_infantil':
        if (rankInf.length > 0) {
          setPhase('ranking_infantil');
          setRankingRevealed(1);
        } else {
          setPhase('bridge_adulto');
        }
        break;
      case 'ranking_infantil':
        if (rankingRevealed < worstInf.length) {
          setRankingRevealed((n) => n + 1);
        } else {
          setPhase('bridge_adulto');
        }
        break;
      case 'bridge_adulto':
        if (salasAdulto.length > 0) {
          setSalaIndex(0);
          setPhase('sala_adulto');
        } else if (rankAd.length > 0) {
          setRankingRevealed(0);
          setPhase('ranking_intro_adulto');
        } else {
          setPhase('end');
        }
        break;
      case 'sala_adulto':
        if (salaIndex < salasAdulto.length - 1) {
          setSalaIndex((i) => i + 1);
        } else {
          setRankingRevealed(0);
          setPhase('ranking_intro_adulto');
        }
        break;
      case 'ranking_intro_adulto':
        if (rankAd.length > 0) {
          setPhase('ranking_adulto');
          setRankingRevealed(1);
        } else {
          setPhase('end');
        }
        break;
      case 'ranking_adulto':
        if (rankingRevealed < worstAd.length) {
          setRankingRevealed((n) => n + 1);
        } else {
          setPhase('end');
        }
        break;
      default:
        break;
    }
  }, [
    loading,
    error,
    phase,
    salaIndex,
    salasInfantil.length,
    salasAdulto.length,
    rankInf.length,
    rankAd.length,
    rankingRevealed,
    worstInf.length,
    worstAd.length,
  ]);

  const goBack = useCallback(() => {
    switch (phase) {
      case 'sala_infantil':
        if (salaIndex > 0) {
          setSalaIndex((i) => i - 1);
        } else {
          setPhase('intro');
        }
        break;
      case 'ranking_intro_infantil':
        if (salasInfantil.length > 0) {
          setPhase('sala_infantil');
          setSalaIndex(salasInfantil.length - 1);
        } else {
          setPhase('intro');
        }
        break;
      case 'ranking_infantil':
        if (rankingRevealed <= 1) {
          setRankingRevealed(0);
          setPhase('ranking_intro_infantil');
        } else {
          setRankingRevealed((n) => Math.max(1, n - 1));
        }
        break;
      case 'bridge_adulto':
        if (rankInf.length > 0) {
          setPhase('ranking_infantil');
          setRankingRevealed(worstInf.length);
        } else if (salasInfantil.length > 0) {
          setPhase('sala_infantil');
          setSalaIndex(salasInfantil.length - 1);
        } else {
          setPhase('intro');
        }
        break;
      case 'sala_adulto':
        if (salaIndex > 0) {
          setSalaIndex((i) => i - 1);
        } else {
          setPhase('bridge_adulto');
        }
        break;
      case 'ranking_intro_adulto':
        if (salasAdulto.length > 0) {
          setPhase('sala_adulto');
          setSalaIndex(salasAdulto.length - 1);
        } else {
          setPhase('bridge_adulto');
        }
        break;
      case 'ranking_adulto':
        if (rankingRevealed <= 1) {
          setRankingRevealed(0);
          setPhase('ranking_intro_adulto');
        } else {
          setRankingRevealed((n) => Math.max(1, n - 1));
        }
        break;
      case 'end':
        if (rankAd.length > 0) {
          setPhase('ranking_adulto');
          setRankingRevealed(worstAd.length);
        } else if (salasAdulto.length > 0) {
          setPhase('sala_adulto');
          setSalaIndex(salasAdulto.length - 1);
        } else {
          setPhase('bridge_adulto');
        }
        break;
      default:
        break;
    }
  }, [
    phase,
    salaIndex,
    salasInfantil.length,
    salasAdulto.length,
    rankInf.length,
    rankAd.length,
    rankingRevealed,
    worstInf.length,
    worstAd.length,
  ]);

  const handlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    pointerStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }, []);

  const handlePointerCancel = useCallback(() => {
    pointerStartRef.current = null;
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const start = pointerStartRef.current;
      pointerStartRef.current = null;
      if (!start) return;

      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const t = Date.now();
      const duration = t - start.t;
      if (t - lastPointerNavRef.current < 280) return;

      const swipeH =
        duration < 420 && Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.1;
      if (swipeH) {
        lastPointerNavRef.current = t;
        if (dx > 0) goBack();
        else advance();
        return;
      }

      const tap = duration < 520 && Math.abs(dx) < 28 && Math.abs(dy) < 28;
      if (tap) {
        lastPointerNavRef.current = t;
        advance();
      }
    },
    [advance, goBack]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ' || e.key === 'Enter' || e.key === 'ArrowRight') {
        e.preventDefault();
        advance();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      } else if (e.key === 'Escape') {
        window.close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, goBack]);

  const currentSalaList =
    phase === 'sala_infantil'
      ? salasInfantil
      : phase === 'sala_adulto'
        ? salasAdulto
        : [];
  const currentSala = currentSalaList[salaIndex];
  const rows = currentSala ? presencasBySala[String(currentSala.id)] || [] : [];
  const agg = aggregateRows(rows);
  const matriculadosSala = Number(currentSala?.total_alunos) || 0;
  const aprovSala = aproveitamentoSala(agg, matriculadosSala);
  const mediaPtsSala =
    agg.registros > 0 ? Math.round((agg.somaPontos / agg.registros) * 100) / 100 : 0;
  const ofertaTotalSalaPeriodo = Number(
    ranking.find((r) => String(r.sala_id) === String(currentSala?.id))?.soma_oferta_periodo ?? 0
  );

  const visibleRankingInf = worstInf.slice(0, rankingRevealed);
  const visibleRankingAd = worstAd.slice(0, rankingRevealed);

  const maxTaxaInf = worstInf[worstInf.length - 1]?.taxa_comparecimento || 1;
  const maxTaxaAd = worstAd[worstAd.length - 1]?.taxa_comparecimento || 1;

  if (loading) {
    return (
      <div className="ebd-apresentacao flex min-h-screen items-center justify-center bg-slate-950 text-slate-100">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
          <p className="mt-6 text-lg text-slate-400">Carregando apresentação…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ebd-apresentacao flex min-h-screen flex-col items-center justify-center bg-slate-950 px-6 text-center text-slate-100">
        <p className="text-xl text-red-300">{error}</p>
        <p className="mt-4 text-slate-500">Feche a aba ou pressione Esc.</p>
      </div>
    );
  }

  return (
    <div
      className="ebd-apresentacao min-h-[100dvh] touch-manipulation select-none bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-50"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      role="presentation"
    >
      <div className="mx-auto flex min-h-[100dvh] max-w-5xl flex-col px-4 py-6 sm:px-6 sm:py-10 md:px-10 md:py-14">
        <header className="mb-4 flex shrink-0 flex-col gap-1 border-b border-slate-700/60 pb-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between sm:pb-4">
          <span className="font-heading text-xs font-semibold tracking-wide text-sky-400 sm:text-sm">Igreja360 · EBD</span>
          <span className="text-[11px] text-slate-500 sm:text-xs">{periodoLabel(periodo, periodo === 'dia' ? dataRef : null)}</span>
        </header>

        <main className="flex min-h-0 flex-1 flex-col justify-center">
          {phase === 'intro' && (
            <div className="animate-fade-in text-center">
              <h1 className="font-heading text-4xl font-bold tracking-tight text-white md:text-5xl">Apresentação</h1>
              <p className="mt-4 text-xl text-slate-400 md:text-2xl">
                Infantil (salas e ranking) · depois adultos
              </p>
              <p className="mt-8 text-base text-slate-400 sm:mt-10">Toque na tela para avançar. Deslize para a esquerda para avançar ou para a direita para voltar.</p>
              <p className="mt-4 hidden text-slate-500 sm:block">
                Teclado: <kbd className="rounded bg-slate-800 px-2 py-1 text-slate-300">Espaço</kbd>,{' '}
                <kbd className="rounded bg-slate-800 px-2 py-1 text-slate-300">Enter</kbd> ou{' '}
                <kbd className="rounded bg-slate-800 px-2 py-1 text-slate-300">→</kbd> avançar ·{' '}
                <kbd className="rounded bg-slate-800 px-2 py-1 text-slate-300">←</kbd> voltar ·{' '}
                <kbd className="rounded bg-slate-800 px-2 py-1 text-slate-300">Esc</kbd> fechar
              </p>
            </div>
          )}

          {(phase === 'sala_infantil' || phase === 'sala_adulto') && currentSala && (
            <div className="animate-fade-in">
              <p className="text-center text-sm uppercase tracking-widest text-violet-300/90">
                {phase === 'sala_infantil' ? 'Departamento infantil' : 'Departamento adulto'}
              </p>
              <p className="text-center text-sm uppercase tracking-widest text-slate-500">
                Sala {salaIndex + 1} de {currentSalaList.length}
              </p>
              <h2 className="mt-2 text-center font-heading text-4xl font-bold text-white md:text-5xl">
                {currentSala.nome}
              </h2>
              {currentSala.faixa_etaria && (
                <p className="mt-2 text-center text-lg text-slate-400">{currentSala.faixa_etaria}</p>
              )}

              <div className="mx-auto mt-12 grid max-w-3xl grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                <StatBox
                  label="Matriculados"
                  value={currentSala?.total_alunos != null && currentSala.total_alunos !== '' ? matriculadosSala : '—'}
                  accent="sky"
                />
                <StatBox label="Presenças" value={agg.presentes} accent="emerald" />
                <StatBox label="Bíblias" value={agg.biblias} accent="amber" />
                <StatBox label="Revistas" value={agg.revistas} accent="violet" />
                <StatBox label="Lições estudadas" value={agg.licoes} accent="teal" />
                <StatBox label="Visitantes" value={agg.visitantes} accent="orange" />
                <StatBox
                  label="Aproveitamento"
                  value={`${Math.round(aprovSala * 100)}%`}
                  accent="slate"
                />
                <StatBox label="Média de pontuação" value={mediaPtsSala} accent="sky" />
                <StatBox
                  label="Oferta total"
                  value={ofertaTotalSalaPeriodo.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                  accent="sky"
                />
              </div>
            </div>
          )}

          {phase === 'ranking_intro_infantil' && (
            <div className="animate-fade-in text-center">
              <p className="text-sm uppercase tracking-widest text-violet-300">Infantil</p>
              <h2 className="mt-2 font-heading text-4xl font-bold text-white md:text-5xl">Ranking das salas</h2>
              <p className="mt-6 text-base text-slate-400 sm:text-lg">
                {rankInf.length
                  ? 'Da última para a primeira colocação. Toque na tela para começar.'
                  : 'Sem ranking infantil neste período. Toque para continuar.'}
              </p>
            </div>
          )}

          {phase === 'ranking_infantil' && (
            <div className="animate-fade-in flex min-h-0 flex-1 flex-col py-2 sm:min-h-[580px]">
              <p className="text-center text-xs uppercase tracking-widest text-violet-300 sm:text-sm">Infantil</p>
              <h2 className="text-center font-heading text-2xl font-bold text-white sm:text-3xl md:text-4xl">Ranking</h2>
              <p className="mt-1 text-center text-xs text-slate-500 sm:text-sm">
                {Math.min(rankingRevealed, worstInf.length)} de {worstInf.length} revelado{worstInf.length !== 1 ? 's' : ''}
                <span className="mt-1 block text-slate-600 sm:hidden">Toque para revelar o próximo</span>
              </p>
              <RankingBars items={visibleRankingInf} maxTaxa={maxTaxaInf} />
            </div>
          )}

          {phase === 'bridge_adulto' && (
            <div className="animate-fade-in text-center">
              <p className="text-sm uppercase tracking-widest text-sky-400">Próxima parte</p>
              <h2 className="mt-4 font-heading text-4xl font-bold text-white md:text-5xl">Departamento adulto</h2>
              <p className="mt-6 text-lg text-slate-400">Salas e ranking das classes adultas.</p>
            </div>
          )}

          {phase === 'ranking_intro_adulto' && (
            <div className="animate-fade-in text-center">
              <p className="text-sm uppercase tracking-widest text-sky-400">Adulto</p>
              <h2 className="mt-2 font-heading text-4xl font-bold text-white md:text-5xl">Ranking das salas</h2>
              <p className="mt-6 text-base text-slate-400 sm:text-lg">
                {rankAd.length
                  ? 'Da última para a primeira colocação. Toque na tela para começar.'
                  : 'Sem ranking adulto neste período. Toque para continuar.'}
              </p>
            </div>
          )}

          {phase === 'ranking_adulto' && (
            <div className="animate-fade-in flex min-h-0 flex-1 flex-col py-2 sm:min-h-[580px]">
              <p className="text-center text-xs uppercase tracking-widest text-sky-400 sm:text-sm">Adulto</p>
              <h2 className="text-center font-heading text-2xl font-bold text-white sm:text-3xl md:text-4xl">Ranking</h2>
              <p className="mt-1 text-center text-xs text-slate-500 sm:text-sm">
                {Math.min(rankingRevealed, worstAd.length)} de {worstAd.length} revelado{worstAd.length !== 1 ? 's' : ''}
                <span className="mt-1 block text-slate-600 sm:hidden">Toque para revelar o próximo</span>
              </p>
              <RankingBars items={visibleRankingAd} maxTaxa={maxTaxaAd} />
            </div>
          )}

          {phase === 'end' && (
            <div className="animate-fade-in text-center">
              <p className="font-heading text-3xl font-bold text-white md:text-4xl">Obrigado!</p>
              <p className="mt-4 text-slate-400">Fim da apresentação. Pressione Esc para fechar a aba.</p>
            </div>
          )}
        </main>

        <footer className="mt-auto shrink-0 pt-10 text-center text-xs text-slate-600">
          Igreja360 · Escola Bíblica Dominical
        </footer>
      </div>
    </div>
  );
};

function BarCol({ item, maxTaxa }) {
  const [mounted, setMounted] = useState(false);
  const taxa = Number(item.taxa_comparecimento ?? item.aproveitamento ?? 0);
  const denom = maxTaxa > 0 ? maxTaxa : 1;
  const pct = Math.max(6, (taxa / denom) * 100);

  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(id);
  }, []);

  const barGradient =
    item.posicao === 1
      ? 'from-amber-600 to-amber-300 shadow-amber-500/40'
      : item.posicao === 2
      ? 'from-slate-500 to-slate-300 shadow-slate-400/30'
      : item.posicao === 3
      ? 'from-orange-700 to-orange-400 shadow-orange-500/30'
      : 'from-sky-700 to-sky-400 shadow-sky-500/20';

  const posColor =
    item.posicao === 1 ? 'text-amber-400' : item.posicao <= 3 ? 'text-orange-400' : 'text-slate-400';

  const barAreaH = 'clamp(6rem, 22dvh, 14rem)';

  const barGradientH =
    item.posicao === 1
      ? 'from-amber-600 to-amber-300 shadow-amber-500/30'
      : item.posicao === 2
      ? 'from-slate-500 to-slate-300 shadow-slate-400/25'
      : item.posicao === 3
      ? 'from-orange-700 to-orange-400 shadow-orange-500/25'
      : 'from-sky-700 to-sky-400 shadow-sky-500/20';

  return (
    <div className="flex w-full shrink-0 flex-col gap-2 rounded-xl border border-slate-700/50 bg-slate-900/20 p-2.5 min-w-0 sm:w-auto sm:max-w-[220px] sm:flex-1 sm:items-center sm:border-0 sm:bg-transparent sm:p-0 sm:gap-2">
      <div className="flex flex-row items-start justify-between gap-2 sm:flex-col sm:items-center sm:justify-start">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:items-center">
          <span className={`font-heading text-base font-bold tabular-nums sm:text-xl md:text-2xl ${posColor}`}>
            {item.posicao}º
          </span>
          <span className="line-clamp-2 text-left text-[11px] font-semibold leading-tight text-white sm:text-center sm:text-sm md:text-base">
            {item.sala_nome}
          </span>
          {item.faixa_etaria && (
            <span className="text-left text-[10px] text-slate-400 sm:text-center sm:text-xs">{item.faixa_etaria}</span>
          )}
        </div>
        <span className="shrink-0 text-xs font-semibold tabular-nums text-sky-300 sm:hidden">
          {Math.round(taxa * 100)}%
        </span>
      </div>

      {/* Mobile: gráfico horizontal */}
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-slate-800/90 sm:hidden">
        <div
          className={`absolute left-0 top-0 h-full rounded-full bg-gradient-to-r shadow-md ${barGradientH}`}
          style={{
            width: mounted ? `${pct}%` : '0%',
            transition: 'width 1s cubic-bezier(0.34, 1.4, 0.64, 1)',
          }}
        />
      </div>

      <RankingSalaMetrics item={item} />

      {/* sm+: barras verticais */}
      <div className="hidden w-full items-end justify-center sm:flex" style={{ height: barAreaH }}>
        <div
          className={`w-full max-w-[4.5rem] rounded-t-xl bg-gradient-to-t shadow-xl sm:max-w-none sm:rounded-t-2xl ${barGradient}`}
          style={{
            height: mounted ? `${pct}%` : '0%',
            transition: 'height 1s cubic-bezier(0.34, 1.4, 0.64, 1)',
          }}
        />
      </div>
    </div>
  );
}

function RankingSalaMetrics({ item }) {
  const aprov = Number(item.aproveitamento ?? item.taxa_comparecimento ?? 0);
  const mat = item.total_matriculados ?? item.matriculados ?? '—';
  const pres = item.total_presencas ?? item.presencas_presente ?? 0;
  const bib = item.total_biblias ?? 0;
  const rev = item.total_revistas ?? 0;
  const lic = item.total_licoes_estudadas ?? 0;
  const vis = item.total_visitantes ?? 0;
  const media = item.pontuacao_media ?? 0;

  const rows = [
    ['Matriculados', mat],
    ['Presenças', pres],
    ['Bíblias', bib],
    ['Revistas', rev],
    ['Lições estudadas', lic],
    ['Visitantes', vis],
    ['Aproveitamento', `${Math.round(aprov * 100)}%`],
    ['Média pontuação', media],
  ];

  return (
    <div className="w-full max-w-none rounded-lg border-0 bg-slate-900/40 px-2 py-1.5 text-left sm:border sm:border-slate-700/60 sm:bg-slate-900/50">
      <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] leading-tight text-slate-300 sm:grid-cols-1 sm:gap-1 sm:text-[10px] md:text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-1 border-b border-slate-700/40 py-0.5 last:border-0">
            <dt className="shrink text-slate-500">{k}</dt>
            <dd className="tabular-nums font-semibold text-slate-100">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function RankingBars({ items, maxTaxa }) {
  return (
    <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-2 pb-4 [-webkit-overflow-scrolling:touch] sm:mt-6 sm:flex-row sm:flex-nowrap sm:items-end sm:justify-center sm:gap-4 sm:overflow-x-auto sm:overflow-y-visible sm:px-4 md:gap-6 md:pb-0">
      {items.map((item) => (
        <BarCol key={item.sala_id} item={item} maxTaxa={maxTaxa} />
      ))}
    </div>
  );
}

function StatBox({ label, value, accent, className = '' }) {
  const ring = {
    sky: 'ring-sky-500/30',
    slate: 'ring-slate-600/40',
    emerald: 'ring-emerald-500/30',
    amber: 'ring-amber-500/30',
    violet: 'ring-violet-500/30',
    orange: 'ring-orange-500/30',
    pink: 'ring-pink-500/30',
    teal: 'ring-teal-500/30',
  }[accent] || 'ring-slate-600/40';

  return (
    <div
      className={`rounded-2xl border border-slate-700/50 bg-slate-800/30 p-5 ring-1 ${ring} ${className}`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 font-heading text-2xl font-bold text-white md:text-3xl">{value}</p>
    </div>
  );
}

export default EbdApresentacao;
